import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search, MapPin, Globe, Star, Send, Building2,
  X, ChevronDown, CheckCircle2, Loader2, Mail,
  Trophy, Medal, Award, TrendingUp, Shield, Zap, Sparkles, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_CONTRACTORS = [
  { org_id: 'org_c4', name: 'Global Expo Group', country: 'US', city: 'New York', tagline: 'Full-service global exhibit solutions', services: ['Turnkey Builds', 'Storage', 'Project Mgmt'], coverage_count: 24, profile_score: 95, proposal_count: 58 },
  { org_id: 'org_c1', name: 'Istanbul Fair Solutions', country: 'TR', city: 'Istanbul', tagline: 'Award-winning stand design since 2008', services: ['Stand Design', 'Modular Systems', 'Lighting'], coverage_count: 12, profile_score: 92, proposal_count: 47 },
  { org_id: 'org_c2', name: 'Berlin Stand GmbH', country: 'DE', city: 'Berlin', tagline: 'Premium European exhibit builder', services: ['Custom Builds', 'AV Integration', 'Transport'], coverage_count: 18, profile_score: 88, proposal_count: 31 },
  { org_id: 'org_c6', name: 'Tokyo Exhibit Co.', country: 'JP', city: 'Tokyo', tagline: 'Asian market exhibition specialists', services: ['Stand Design', 'Digital Displays', 'Rental'], coverage_count: 6, profile_score: 81, proposal_count: 19 },
  { org_id: 'org_c3', name: 'Dubai Expo Builders', country: 'UAE', city: 'Dubai', tagline: 'MENA region specialists for large stands', services: ['Pavilion Design', 'Carpentry', 'Graphics'], coverage_count: 8, profile_score: 79, proposal_count: 22 },
  { org_id: 'org_c5', name: 'Madrid Stand Experts', country: 'ES', city: 'Madrid', tagline: 'Mediterranean stand design specialists', services: ['Custom Design', 'Installation', 'Dismantling'], coverage_count: 10, profile_score: 72, proposal_count: 15 },
];

const MOCK_BRIEFS = [
  { id: 'br_mock_1', title: 'Istanbul Tech Fair 2025 — Stand Design', status: 'published' },
  { id: 'br_mock_2', title: 'Hannover Messe — Modular Stand', status: 'published' },
  { id: 'br_mock_3', title: 'Dubai Expo Corporate Pavilion', status: 'published' },
];

const getTier = (score) => {
  if (score >= 90) return { label: 'Platinum', short: 'PLT', bg: 'bg-purple-600', text: 'text-white', border: 'border-purple-200', light: 'bg-purple-50', lightText: 'text-purple-700', glow: 'shadow-purple-100' };
  if (score >= 80) return { label: 'Gold', short: 'GLD', bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-200', light: 'bg-amber-50', lightText: 'text-amber-700', glow: 'shadow-amber-100' };
  if (score >= 70) return { label: 'Silver', short: 'SLV', bg: 'bg-slate-400', text: 'text-white', border: 'border-slate-200', light: 'bg-slate-50', lightText: 'text-slate-600', glow: 'shadow-slate-100' };
  return { label: 'Bronze', short: 'BRZ', bg: 'bg-orange-600', text: 'text-white', border: 'border-orange-200', light: 'bg-orange-50', lightText: 'text-orange-700', glow: 'shadow-orange-100' };
};

const RANK_META = [
  { icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-300', size: 'scale-105', shadow: 'shadow-xl shadow-amber-100' },
  { icon: Medal, color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-300', size: 'scale-100', shadow: 'shadow-lg shadow-slate-100' },
  { icon: Award, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-300', size: 'scale-100', shadow: 'shadow-lg shadow-orange-100' },
];

// ─── Invite Modal ───────────────────────────────────────────────────────────
const InviteModal = ({ contractor, onClose, onInvited, token, isMockMode }) => {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [selectedBriefId, setSelectedBriefId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [briefsLoading, setBriefsLoading] = useState(true);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (isMockMode) { setBriefs(MOCK_BRIEFS); setBriefsLoading(false); return; }
    axios.get(`${API}/exhibitor/briefs?status=OPEN&limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setBriefs((r.data?.items || []).map(b => ({ id: b.id, title: `${b.event?.show || 'Brief'} — ${b.event?.city || ''}`, status: b.status }))))
      .catch(() => setBriefs([]))
      .finally(() => setBriefsLoading(false));
  }, [token, isMockMode]);

  const handleSend = async () => {
    if (!selectedBriefId) { toast.error('Lütfen bir brief seçin'); return; }
    setLoading(true);
    try {
      if (!isMockMode) {
        await axios.post(`${API}/exhibitor/briefs/${selectedBriefId}/invite`, { contractor_org_id: contractor.org_id, message }, { headers: { Authorization: `Bearer ${token}` } });
      } else { await new Promise(r => setTimeout(r, 800)); }
      setSent(true);
      toast.success(`${contractor.name} başarıyla davet edildi!`);
      setTimeout(() => { onInvited && onInvited(contractor.org_id); }, 1800);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Davet gönderilemedi';
      toast.error(detail.includes('Already invited') ? 'Bu yüklenici zaten davet edilmiş' : detail);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="invite-modal" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-base">{contractor.name.charAt(0)}</div>
            <div>
              <p className="font-bold text-sm text-gray-900">{contractor.name}</p>
              <p className="text-xs text-gray-500">{contractor.city}{contractor.city && contractor.country ? ', ' : ''}{contractor.country}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        {sent ? (
          <div className="px-6 py-10 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
            <p className="font-bold text-gray-900 mb-1">Davet Gönderildi!</p>
            <p className="text-sm text-gray-500">{contractor.name} bildirim alacak ve yakında yanıt verecek.</p>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Brief Seç <span className="text-red-500">*</span></p>
              {briefsLoading ? (
                <div className="h-10 bg-gray-100 animate-pulse rounded-lg" />
              ) : briefs.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">Henüz aktif brief yok</p>
                  <Button size="sm" onClick={() => { onClose(); navigate('/app/exhibitor/briefs'); }} className="text-xs h-7 bg-blue-600 hover:bg-blue-700 text-white gap-1">
                    <Send className="h-3 w-3" />Brief Oluştur
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <select value={selectedBriefId} onChange={e => setSelectedBriefId(e.target.value)} data-testid="invite-brief-select" className="w-full appearance-none px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8">
                    <option value="">— Brief seçin —</option>
                    {briefs.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Mesaj <span className="text-gray-400 font-normal">(isteğe bağlı)</span></p>
              <textarea value={message} onChange={e => setMessage(e.target.value)} data-testid="invite-message-textarea" rows={3} placeholder="Projeniz hakkında kısa bir mesaj yazın..." className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button onClick={handleSend} disabled={loading || !selectedBriefId || briefs.length === 0} data-testid="send-invite-btn" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {loading ? 'Gönderiliyor...' : 'Davet Gönder'}
              </Button>
              <Button variant="outline" onClick={onClose} className="px-4">İptal</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Podium Card (top 3) ────────────────────────────────────────────────────
const PodiumCard = ({ contractor, rank, onInvite, isInvited }) => {
  const meta = RANK_META[rank - 1];
  const tier = getTier(contractor.profile_score);
  const RankIcon = meta.icon;

  return (
    <div
      data-testid={`podium-card-${contractor.org_id}`}
      className={`relative flex flex-col items-center rounded-2xl border-2 ${meta.border} ${meta.shadow} ${meta.size} bg-white transition-all hover:-translate-y-1 hover:shadow-2xl p-5 ${rank === 1 ? 'pt-7' : 'pt-5'}`}
      style={{ minHeight: rank === 1 ? '280px' : '260px' }}
    >
      {/* Crown / Medal */}
      <div className={`absolute -top-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full ${meta.bg} border-2 ${meta.border} flex items-center justify-center shadow-md`}>
        <RankIcon className={`h-5 w-5 ${meta.color}`} />
      </div>

      {/* Rank Number */}
      <div className={`text-5xl font-black leading-none mb-2 ${meta.color}`}>#{rank}</div>

      {/* Avatar */}
      <div className={`w-14 h-14 rounded-2xl ${tier.bg} flex items-center justify-center text-white font-black text-2xl mb-3 shadow-sm`}>
        {contractor.name.charAt(0)}
      </div>

      {/* Tier Badge */}
      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${tier.bg} ${tier.text} tracking-widest uppercase mb-2`}>{tier.label}</span>

      {/* Name + Location */}
      <p className="text-sm font-bold text-gray-900 text-center leading-tight mb-1">{contractor.name}</p>
      <p className="text-xs text-gray-400 flex items-center gap-1 mb-3">
        <MapPin className="h-3 w-3 flex-shrink-0" />{contractor.city}, {contractor.country}
      </p>

      {/* Score Bar */}
      <div className="w-full mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-gray-400 font-medium">Profil Skoru</span>
          <span className={`text-sm font-black ${tier.lightText}`}>{contractor.profile_score}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${tier.bg}`} style={{ width: `${contractor.profile_score}%` }} />
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 text-center w-full mb-4">
        <div className={`flex-1 rounded-xl py-2 ${tier.light}`}>
          <p className={`text-sm font-black ${tier.lightText}`}>{contractor.proposal_count}</p>
          <p className="text-[9px] text-gray-400 uppercase tracking-wide">Teklif</p>
        </div>
        <div className="flex-1 rounded-xl py-2 bg-blue-50">
          <p className="text-sm font-black text-blue-700">{contractor.coverage_count}</p>
          <p className="text-[9px] text-gray-400 uppercase tracking-wide">Ülke</p>
        </div>
      </div>

      {/* Invite + Profile Buttons */}
      <div className="w-full flex gap-2">
        <Button
          size="sm"
          onClick={() => !isInvited && onInvite(contractor)}
          disabled={isInvited}
          data-testid={`invite-contractor-${contractor.org_id}`}
          className={`flex-1 h-8 text-xs gap-1.5 transition-all ${isInvited ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-default' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          {isInvited ? <><CheckCircle2 className="h-3.5 w-3.5" />Davet Edildi</> : <><Mail className="h-3.5 w-3.5" />Davet Et</>}
        </Button>
        <Link
          to={`/contractor/${contractor.slug || contractor.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`profile-btn-${contractor.org_id}`}
          className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shrink-0"
          title="Profil Görüntüle"
        >
          <Eye className="h-3.5 w-3.5 text-gray-500" />
        </Link>
      </div>
    </div>
  );
};

// ─── Ranked Row (list below top 3) ─────────────────────────────────────────
const RankedRow = ({ contractor, rank, onInvite, isInvited }) => {
  const tier = getTier(contractor.profile_score);

  return (
    <div
      data-testid={`contractor-card-${contractor.org_id}`}
      className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-4 py-3.5 hover:border-blue-200 hover:shadow-sm transition-all group"
    >
      {/* Rank */}
      <div className="w-8 text-center shrink-0">
        <span className="text-lg font-black text-gray-300 group-hover:text-blue-400 transition-colors">#{rank}</span>
      </div>

      {/* Avatar */}
      <div className={`w-10 h-10 rounded-xl ${tier.bg} flex items-center justify-center text-white font-bold text-base shrink-0`}>
        {contractor.name.charAt(0)}
      </div>

      {/* Name & Location */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-gray-900 truncate">{contractor.name}</p>
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${tier.bg} ${tier.text} uppercase tracking-widest shrink-0`}>{tier.label}</span>
        </div>
        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
          <MapPin className="h-3 w-3 shrink-0" />{contractor.city}, {contractor.country}
          <span className="mx-1 text-gray-200">·</span>
          <Globe className="h-3 w-3 shrink-0" />{contractor.coverage_count} ülke
        </p>
      </div>

      {/* Services chips (desktop only) */}
      <div className="hidden lg:flex items-center gap-1.5 shrink-0">
        {contractor.services.slice(0, 2).map((s, i) => (
          <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${tier.border} ${tier.light} ${tier.lightText}`}>{s}</span>
        ))}
        {contractor.services.length > 2 && <span className="text-[10px] text-gray-400">+{contractor.services.length - 2}</span>}
      </div>

      {/* Score */}
      <div className="hidden sm:flex flex-col items-center shrink-0 w-16">
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-1">
          <div className={`h-full rounded-full ${tier.bg}`} style={{ width: `${contractor.profile_score}%` }} />
        </div>
        <span className={`text-xs font-bold ${tier.lightText}`}>{contractor.profile_score}%</span>
      </div>

      {/* Proposals */}
      <div className="hidden sm:flex flex-col items-center shrink-0 w-16 text-center">
        <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400 mb-0.5" />
        <span className="text-xs font-bold text-gray-700">{contractor.proposal_count}</span>
        <span className="text-[9px] text-gray-400">teklif</span>
      </div>

      {/* Invite + Profile */}
      <div className="flex items-center gap-1.5 shrink-0">
        <Button
          size="sm"
          onClick={() => !isInvited && onInvite(contractor)}
          disabled={isInvited}
          data-testid={`invite-contractor-row-${contractor.org_id}`}
          className={`h-8 text-xs gap-1 transition-all ${isInvited ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-default' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          {isInvited ? <><CheckCircle2 className="h-3 w-3" />Davet Edildi</> : <><Mail className="h-3 w-3" />Davet Et</>}
        </Button>
        <Link
          to={`/contractor/${contractor.slug || contractor.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`profile-link-row-${contractor.org_id}`}
          className="h-8 w-8 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 hover:border-blue-300 transition-all"
          title="Profil Görüntüle"
        >
          <Eye className="h-3.5 w-3.5 text-gray-500" />
        </Link>
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const FindContractors = () => {
  const { token } = useAuth();
  const { isMockMode } = useDataMode();
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteTarget, setInviteTarget] = useState(null);
  const [countryFilter, setCountryFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [invitedOrgs, setInvitedOrgs] = useState(new Set());
  const [featuredOrgs, setFeaturedOrgs] = useState([]);

  useEffect(() => {
    if (isMockMode) {
      setTimeout(() => { setContractors(MOCK_CONTRACTORS); setLoading(false); }, 400);
      return;
    }
    // Public endpoint — no auth needed, includes featured status
    axios.get(`${API}/public/contractors`)
      .then(r => {
        const items = r.data?.items || [];
        setContractors(items.length > 0 ? items : MOCK_CONTRACTORS);
        setFeaturedOrgs(items.filter(c => c.is_featured));
      })
      .catch(() => { setContractors(MOCK_CONTRACTORS); })
      .finally(() => setLoading(false));
  }, [isMockMode, token]);

  // Sort by score descending
  const sortedAll = [...contractors].sort((a, b) => b.profile_score - a.profile_score);

  const allCountries = [...new Set(sortedAll.map(c => c.country).filter(Boolean))].sort();
  const allServices = [...new Set(sortedAll.flatMap(c => c.services))].sort();

  const filtered = sortedAll.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.city || '').toLowerCase().includes(search.toLowerCase()) || c.country.toLowerCase().includes(search.toLowerCase()) || c.services.some(s => s.toLowerCase().includes(search.toLowerCase()));
    const matchCountry = countryFilter === 'all' || c.country === countryFilter;
    const matchService = serviceFilter === 'all' || c.services.includes(serviceFilter);
    return matchSearch && matchCountry && matchService;
  });

  const hasFilters = search || countryFilter !== 'all' || serviceFilter !== 'all';
  const top3 = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const tierCounts = {
    Platinum: filtered.filter(c => c.profile_score >= 90).length,
    Gold: filtered.filter(c => c.profile_score >= 80 && c.profile_score < 90).length,
    Silver: filtered.filter(c => c.profile_score >= 70 && c.profile_score < 80).length,
    Bronze: filtered.filter(c => c.profile_score < 70).length,
  };

  return (
    <div className="space-y-6" data-testid="find-contractors-page">
      {inviteTarget && (
        <InviteModal
          contractor={inviteTarget}
          onClose={() => setInviteTarget(null)}
          onInvited={(orgId) => { setInvitedOrgs(prev => new Set([...prev, orgId])); setInviteTarget(null); }}
          token={token}
          isMockMode={isMockMode}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-5 w-5 text-amber-500" />
            <h1 className="text-xl sm:text-2xl font-black text-gray-900">Yüklenici Lider Tablosu</h1>
          </div>
          <p className="text-sm text-gray-500">Doğrulanmış stand üreticileri ve yükleniciler — performans sıralaması</p>
        </div>
        <Button onClick={() => navigate('/app/exhibitor/briefs')} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
          <Send className="h-4 w-4" />Brief Yayınla
        </Button>
      </div>

      {/* Featured Contractors Banner */}
      {featuredOrgs.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 p-5 shadow-sm shadow-amber-100">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-black text-amber-700 uppercase tracking-widest">Sponsorlu · Öne Çıkan Yükleniciler</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {featuredOrgs.map(c => (
              <div key={c.org_id} className="flex items-center gap-3 bg-white rounded-xl border border-amber-200 px-4 py-3 hover:border-amber-400 hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center text-white font-black text-base shrink-0">
                  {(c.name || c.display_name || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{c.name || c.display_name}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />{c.city}{c.city && c.country ? ', ' : ''}{c.country}
                  </p>
                </div>
                <Button size="sm" onClick={() => setInviteTarget(c)} className="shrink-0 h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white gap-1">
                  <Mail className="h-3 w-3" />Davet
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tier Summary KPIs */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Platinum', count: tierCounts.Platinum, bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
            { label: 'Gold', count: tierCounts.Gold, bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
            { label: 'Silver', count: tierCounts.Silver, bg: 'bg-slate-50', text: 'text-slate-600', dot: 'bg-slate-400' },
            { label: 'Bronze', count: tierCounts.Bronze, bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
          ].map(t => (
            <div key={t.label} className={`rounded-xl ${t.bg} px-4 py-3 flex items-center gap-3`}>
              <div className={`w-2.5 h-2.5 rounded-full ${t.dot} shrink-0`} />
              <div>
                <p className={`text-lg font-black leading-none ${t.text}`}>{t.count}</p>
                <p className="text-xs text-gray-400 font-medium">{t.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search + Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="İsim, şehir, ülke veya hizmet ara..." data-testid="find-contractors-search"
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={countryFilter} onChange={e => setCountryFilter(e.target.value)} data-testid="country-filter-select" className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Tüm Ülkeler</option>
            {allCountries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)} data-testid="service-filter-select" className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="all">Tüm Hizmetler</option>
            {allServices.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {hasFilters && (
            <button onClick={() => { setSearch(''); setCountryFilter('all'); setServiceFilter('all'); }} data-testid="clear-contractor-filters" className="flex items-center gap-1 px-3 py-2 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg border border-red-200">
              <X className="h-3.5 w-3.5" />Temizle
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">Yüklenici bulunamadı</p>
          <p className="text-xs mt-1">Farklı bir arama terimi deneyin</p>
        </div>
      ) : (
        <>
          {/* ─── PODIUM: Top 3 ─── */}
          {top3.length >= 2 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-widest px-3">En İyi 3 Yüklenici</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-200 to-transparent" />
              </div>
              {/* Podium layout: #2, #1 (elevated), #3 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end mb-6">
                {/* #2 left */}
                {top3[1] && (
                  <div className="sm:mt-4">
                    <PodiumCard contractor={top3[1]} rank={2} onInvite={setInviteTarget} isInvited={invitedOrgs.has(top3[1].org_id)} />
                  </div>
                )}
                {/* #1 center — elevated */}
                <div className="order-first sm:order-none">
                  <PodiumCard contractor={top3[0]} rank={1} onInvite={setInviteTarget} isInvited={invitedOrgs.has(top3[0].org_id)} />
                </div>
                {/* #3 right */}
                {top3[2] && (
                  <div className="sm:mt-8">
                    <PodiumCard contractor={top3[2]} rank={3} onInvite={setInviteTarget} isInvited={invitedOrgs.has(top3[2].org_id)} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Ranked List ─── */}
          {rest.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-600">Tam Sıralama</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{filtered.length} yüklenici</span>
              </div>

              {/* Table Header */}
              <div className="hidden sm:grid sm:grid-cols-[40px_44px_1fr_180px_80px_80px_100px] gap-3 px-4 mb-1.5">
                {['Sıra', '', 'Yüklenici', 'Hizmetler', 'Skor', 'Teklif', ''].map((h, i) => (
                  <span key={i} className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{h}</span>
                ))}
              </div>

              <div className="space-y-2">
                {/* Show top 3 in list too if we have enough */}
                {top3.length < 3 && filtered.map((c, idx) => (
                  <RankedRow key={c.org_id} contractor={c} rank={idx + 1} onInvite={setInviteTarget} isInvited={invitedOrgs.has(c.org_id)} />
                ))}
                {top3.length >= 3 && rest.map((c, idx) => (
                  <RankedRow key={c.org_id} contractor={c} rank={idx + 4} onInvite={setInviteTarget} isInvited={invitedOrgs.has(c.org_id)} />
                ))}
              </div>
            </div>
          )}

          {/* If fewer than 3 total, show all in list */}
          {top3.length < 2 && (
            <div className="space-y-2">
              {filtered.map((c, idx) => (
                <RankedRow key={c.org_id} contractor={c} rank={idx + 1} onInvite={setInviteTarget} isInvited={invitedOrgs.has(c.org_id)} />
              ))}
            </div>
          )}
        </>
      )}

      <p className="text-xs text-gray-400 text-center pb-4">
        {filtered.length} / {contractors.length} yüklenici gösteriliyor · Brief yayınlayarak da doğrudan teklif alabilirsiniz
      </p>
    </div>
  );
};

export default FindContractors;
